import { languageMap } from "@/components/LanguageMap";
import type { BookSummary } from "@/lib/lumi-api";

// Public Lumi cover art bucket (used by the original library). Books without an
// explicit cover_image_url get a deterministic placeholder from this set so the
// grid looks like the original instead of empty rectangles.
const COVER_ART_BASE =
  "https://lumi-public-book-cover-art.s3.amazonaws.com";
const COVER_ART_COUNT = 9;

// Reading-level labels, matching the original library's difficulty map.
const DIFFICULTY_LABELS: Record<string, string> = {
  "1": "Newbie",
  "3": "Beginner",
  "5": "Intermediate",
  "7": "Upper Intermediate",
  "9": "Advanced",
  "12": "Fluent",
};

// Stable hash so a given book always maps to the same placeholder cover.
function stableHash(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash;
}

export function coverImageUrl(book: BookSummary): string {
  if (book.cover_image_url) {
    // External covers (e.g. starter catalog art) are already absolute URLs and
    // pass through unchanged. A relative storage path (extracted at import,
    // e.g. `books/<id>/images/cover.png`) is served by the authenticated cover
    // route, which resolves the file under the storage root.
    if (/^https?:\/\//.test(book.cover_image_url)) {
      return book.cover_image_url;
    }
    // The cover route URL is rendered into a client-visible <img src>, so it must
    // resolve same-origin. Use the public base when configured; otherwise emit a
    // host-less path (same origin). Never the internal LUMI_API_BASE_URL /
    // 127.0.0.1, which would leak the backend host into the browser.
    const base = (process.env.NEXT_PUBLIC_LUMI_API_BASE_URL ?? "").replace(
      /\/$/,
      "",
    );
    return `${base}/api/v1/books/${book.id}/cover`;
  }
  const index = (stableHash(book.id) % COVER_ART_COUNT) + 1;
  return `${COVER_ART_BASE}/lumi_cover_${index}.jpeg`;
}

export function languageLabel(code: string | null | undefined): string | null {
  if (!code) {
    return null;
  }
  const label = (languageMap as Record<string, string>)[code];
  if (label) {
    return label;
  }
  // Fall back to a title-cased version of whatever we were given.
  return code.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function difficultyLabel(
  level: string | null | undefined,
): string | null {
  if (!level) {
    return null;
  }
  return DIFFICULTY_LABELS[level] ?? "Unknown";
}

// A short, human-friendly status badge label + tone for a book/job status.
export function statusTone(status: string): "ready" | "pending" | "error" {
  const normalized = status.toLowerCase();
  if (normalized.includes("fail") || normalized.includes("error")) {
    return "error";
  }
  if (
    normalized === "ready" ||
    normalized === "complete" ||
    normalized === "completed" ||
    normalized === "done"
  ) {
    return "ready";
  }
  return "pending";
}

export interface BookProgress {
  // "importing": row exists but the book hasn't been chunked yet (no real % to
  // show — render an indeterminate fill). "translating": chunks exist and are
  // being translated (show the real percent). "ready"/"error": terminal.
  state: "importing" | "translating" | "ready" | "error";
  // 0–100. Indeterminate while importing; the true ratio while translating.
  percent: number;
  // True when there is no real percentage to show yet (importing phase).
  indeterminate: boolean;
}

// Maps a book's status + chunk counts to what the loading overlay should show.
export function bookProgress(book: {
  status: string;
  total_chunks?: number | null;
  translated_chunks?: number | null;
}): BookProgress {
  const tone = statusTone(book.status);
  if (tone === "ready") {
    return { state: "ready", percent: 100, indeterminate: false };
  }
  if (tone === "error") {
    return { state: "error", percent: 0, indeterminate: false };
  }

  const total = book.total_chunks ?? 0;
  const done = book.translated_chunks ?? 0;
  if (total <= 0) {
    // Chunks not created yet (still importing): nothing real to measure.
    return { state: "importing", percent: 0, indeterminate: true };
  }

  const ratio = Math.max(0, Math.min(1, done / total));
  // Clamp to [5, 99] so the bar always reads as "in progress" — it should never
  // look full until the status itself flips to ready.
  const percent = Math.min(99, Math.max(5, Math.round(ratio * 100)));
  return { state: "translating", percent, indeterminate: false };
}

// Convenience: is this book still being prepared (not readable yet)?
export function isBookPending(book: { status: string }): boolean {
  return statusTone(book.status) === "pending";
}
